/**
 * Backup list / restore wire schemas (CLA-1071).
 *
 * Shared by the api (mgmt report + admin RPCs), clawd (the on-node restore
 * step runner) and web (the admin backups panel), so everything here is a
 * real, decodable `Schema` with no server-side dependencies.
 *
 * Every box-supplied string / array is bounded: the on-node agent is not
 * trusted, and an unbounded report would land verbatim in Valkey and in the
 * admin UI.
 *
 * @since 1.0.0
 */
import { Schema } from "effect";

/**
 * Stable profile publish id used for the restore command's re-apply step.
 *
 * @since 1.0.0
 */
export const BACKUP_RESTORE_PUBLISH_ID = "backup-restore";

/**
 * A FULL restic snapshot id (64 lowercase hex chars). Short ids are refused
 * so a restore can never resolve an ambiguous prefix.
 *
 * @since 1.0.0
 */
export const ResticSnapshotId = Schema.String.pipe(Schema.pattern(/^[0-9a-f]{64}$/));
export type ResticSnapshotId = Schema.Schema.Type<typeof ResticSnapshotId>;

/** A string bounded to `n` chars — used for every box-supplied field. */
const MaxStr = (n: number) => Schema.String.pipe(Schema.maxLength(n));

/**
 * One restic snapshot as reported by the on-node agent.
 *
 * `sizeSource` records where `sizeBytes` came from (the snapshot `summary`
 * block or a `restic stats` call); both are null when the size is unknown.
 * `safety` marks the pre-restore safety snapshots the restore runner takes.
 * `restorable` / `unrestorableReason` carry the box's own verdict.
 *
 * @since 1.0.0
 */
export const BackupSnapshotWire = Schema.Struct({
  id: ResticSnapshotId,
  shortId: MaxStr(256),
  time: Schema.DateTimeUtc,
  paths: Schema.Array(MaxStr(1024)).pipe(Schema.maxItems(16)),
  tags: Schema.Array(MaxStr(256)).pipe(Schema.maxItems(32)),
  hostname: MaxStr(256),
  sizeBytes: Schema.NullOr(Schema.Number),
  sizeSource: Schema.NullOr(Schema.Literal("summary", "stats")),
  safety: Schema.Boolean,
  restorable: Schema.Boolean,
  unrestorableReason: Schema.NullOr(MaxStr(1024)),
});
export type BackupSnapshotWire = Schema.Schema.Type<typeof BackupSnapshotWire>;

/**
 * Why a snapshot listing failed on the box (`tag` is the box-side error tag).
 *
 * @since 1.0.0
 */
export const BackupListError = Schema.Struct({
  tag: Schema.String,
  message: MaxStr(2000),
});
export type BackupListError = Schema.Schema.Type<typeof BackupListError>;

/**
 * Input payload for `mgmt.reportBackupSnapshots`. The on-node agent pushes
 * its full current snapshot listing (or the listing error) in response to a
 * refresh. `installationId` is re-asserted against the authenticated gateway
 * token so a hostile box cannot report snapshots for another install.
 *
 * @since 1.0.0
 */
export const ReportBackupSnapshotsInput = Schema.Struct({
  installationId: Schema.String,
  listedAt: Schema.DateTimeUtc,
  snapshots: Schema.Array(BackupSnapshotWire).pipe(Schema.maxItems(200)),
  error: Schema.NullOr(BackupListError),
});
export type ReportBackupSnapshotsInput = Schema.Schema.Type<typeof ReportBackupSnapshotsInput>;

/**
 * The profile publish to re-apply after a restore swaps state back in.
 *
 * @since 1.0.0
 */
export const RestoreProfileRef = Schema.Struct({
  publishId: Schema.String,
  version: Schema.Int,
});
export type RestoreProfileRef = Schema.Schema.Type<typeof RestoreProfileRef>;

/**
 * Payload of the `backup.restore` agent command.
 *
 * @since 1.0.0
 */
export const BackupRestorePayload = Schema.Struct({
  snapshotId: ResticSnapshotId,
  commandId: Schema.String,
  profile: Schema.NullOr(RestoreProfileRef),
});
export type BackupRestorePayload = Schema.Schema.Type<typeof BackupRestorePayload>;

/**
 * The restore runner's ordered steps (`rollback` only runs on failure).
 *
 * @since 1.0.0
 */
export const BackupRestoreStep = Schema.Literal(
  "preflight",
  "freeze",
  "safety-backup",
  "fetch",
  "promote-sqlite",
  "swap",
  "reapply-profile",
  "start",
  "gate",
  "close-out",
  "rollback",
);
export type BackupRestoreStep = Schema.Schema.Type<typeof BackupRestoreStep>;

/**
 * One restore step transition (`started`, then a terminal
 * `done` / `failed` / `skipped`), reported as a `backup_restore_step`
 * migration event.
 *
 * @since 1.0.0
 */
export const BackupRestoreStepEvent = Schema.Struct({
  commandId: Schema.String,
  snapshotId: Schema.String,
  step: BackupRestoreStep,
  status: Schema.Literal("started", "done", "failed", "skipped"),
  durationMs: Schema.Number,
  detail: Schema.Record({ key: Schema.String, value: Schema.Unknown }),
});
export type BackupRestoreStepEvent = Schema.Schema.Type<typeof BackupRestoreStepEvent>;

/**
 * Why the box refused to start a restore (nothing on disk was touched).
 *
 * @since 1.0.0
 */
export const BackupRestoreRefusalReason = Schema.Literal(
  "unsupported-platform",
  "adopt-mode",
  "restic-unavailable",
  "storage-unconfigured",
  "snapshot-not-found",
  "snapshot-paths-mismatch",
  "insufficient-disk",
  "safety-backup-failed",
  "backup-busy",
);
export type BackupRestoreRefusalReason = Schema.Schema.Type<typeof BackupRestoreRefusalReason>;

const outcomeBase = {
  commandId: Schema.String,
  snapshotId: Schema.String,
  steps: Schema.Array(BackupRestoreStepEvent),
};

/**
 * The restore completed and the gate passed.
 *
 * @since 1.0.0
 */
export const BackupRestored = Schema.TaggedStruct("Restored", {
  ...outcomeBase,
  safetySnapshotId: Schema.String,
});

/**
 * The box refused the restore before touching anything.
 *
 * @since 1.0.0
 */
export const BackupRestoreRefused = Schema.TaggedStruct("Refused", {
  ...outcomeBase,
  reason: BackupRestoreRefusalReason,
  detail: Schema.String,
});

/**
 * A step failed and the box rolled back to the safety snapshot (a rollback
 * only happens after the safety backup, so its id is always known).
 *
 * @since 1.0.0
 */
export const BackupRestoreRolledBack = Schema.TaggedStruct("RolledBack", {
  ...outcomeBase,
  safetySnapshotId: Schema.String,
  failedStep: BackupRestoreStep,
  reason: Schema.String,
});

/**
 * A step failed and the rollback could not restore a working state.
 *
 * @since 1.0.0
 */
export const BackupRestoreWedged = Schema.TaggedStruct("Wedged", {
  ...outcomeBase,
  safetySnapshotId: Schema.NullOr(Schema.String),
  failedStep: BackupRestoreStep,
  reason: Schema.String,
});

/**
 * Terminal restore outcome, reported once per `backup.restore` command as a
 * `backup_restore` migration event.
 *
 * @since 1.0.0
 */
export const BackupRestoreOutcome = Schema.Union(
  BackupRestored,
  BackupRestoreRefused,
  BackupRestoreRolledBack,
  BackupRestoreWedged,
);
export type BackupRestoreOutcome = Schema.Schema.Type<typeof BackupRestoreOutcome>;
